<template>
    <div class="filters-summary" data-testid="filters-summary">
        <VJoyTag
            v-if="selectedFilters.minPrice"
            :label="$t('search.filters.summary.minDailyPrice.label', [priceFormatter.format(selectedFilters.minPrice)])"
            :removable="true"
            variant="secondary"
            data-testid="filters-summary-min-price"
            @remove="filtersStore.setPriceMinFilter(undefined)"
        />
        <VJoyTag
            v-if="selectedFilters.maxPrice"
            :label="$t('search.filters.summary.maxDailyPrice.label', [priceFormatter.format(selectedFilters.maxPrice)])"
            :removable="true"
            variant="secondary"
            data-testid="filters-summary-max-price"
            @remove="filtersStore.setPriceMaxFilter(undefined)"
        />
        <VJoyTag
            v-for="badge in selectedFilters.badges"
            :key="badge"
            :label="getBadgeLabel(badge)"
            :removable="true"
            variant="secondary"
            :data-testid="`filters-summary-badges-filter-${badge}`"
            @remove="filtersStore.toggleBadgeFilter(badge)"
        />
        <VJoyTag
            v-if="
                selectedFilters.experienceLevels.length > 0 &&
                getExperienceLevelsLabel(selectedFilters.experienceLevels.map((experienceLevel) => experienceLevel))
            "
            :label="getExperienceLevelsLabel(selectedFilters.experienceLevels.map((experienceLevel) => experienceLevel))"
            :removable="true"
            variant="secondary"
            data-testid="filters-summary-experience-levels-filter"
            @remove="unselectExperienceLevelsFilter()"
        />
        <VJoyTag
            v-for="specialityId in selectedFilters.specialities"
            :key="specialityId"
            :label="specialityId"
            :removable="true"
            variant="secondary"
            :data-testid="`filters-summary-specialities-filter-${specialityId}`"
            @remove="filtersStore.toggleFilter('specialities', specialityId)"
        />
        <VJoyTag
            v-for="categoryId in selectedFilters.categories"
            :key="categoryId"
            :label="getCategoryLabel(categoryId)"
            :removable="true"
            variant="secondary"
            :data-testid="`filters-summary-categories-filter-${categoryId}`"
            @remove="filtersStore.toggleFilter('categories', categoryId)"
        />
        <VJoyTag
            v-if="selectedLanguagesLabel"
            :label="selectedLanguagesLabel"
            :removable="true"
            variant="secondary"
            data-testid="filters-summary-languages-filter"
            @remove="unselectLanguagesFilter()"
        />
        <VJoyTag
            v-for="businessSectorHumanId in selectedFilters.businessSectors"
            :key="businessSectorHumanId"
            :label="getBusinessSectorLabel(businessSectorHumanId)"
            :removable="true"
            variant="secondary"
            :data-testid="`filters-summary-business-sectors-filter-${businessSectorHumanId}`"
            @remove="filtersStore.toggleFilter('businessSectors', businessSectorHumanId)"
        />
        <VJoyTag
            v-for="adminFilterName in selectedFilters.adminFilter"
            :key="adminFilterName"
            :label="getAdminFilterLabel(adminFilterName)"
            :removable="true"
            variant="secondary"
            class="admin-filter"
            :data-testid="`filters-summary-admin-filter-${adminFilterName}`"
            @remove="filtersStore.toggleAdminFilter(adminFilterName)"
        />
    </div>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import type {Ref} from 'vue';
    import {storeToRefs} from 'pinia';
    import {usePriceFormatter, useTranslation} from '#imports';
    import {VJoyTag} from '@maltjoy/core-vue';
    import type {RetrieveAvailableFiltersAdminFilterEnum, RetrieveAvailableFiltersBadgeEnum, RetrieveAvailableFiltersExpEnum} from '@api';
    import {useFiltersStore} from '../stores/filters.store';
    import type {TranslationKey} from '#build/i18n';

    const filtersStore = useFiltersStore();
    const {availableSearchFilters, selectedFilters} = storeToRefs(filtersStore);

    const {t} = useTranslation();

    const priceFormatter = usePriceFormatter(availableSearchFilters.value?.priceHistogram?.currency);

    const selectedLanguagesLabel: Ref<string | undefined> = computed(() => {
        const selectedLanguages = selectedFilters.value?.languages ?? [];
        const availableLanguages = availableSearchFilters.value?.languages ?? [];

        const selectedLabels = availableLanguages.filter((lang) => selectedLanguages.includes(lang.key)).map((lang) => lang.label);

        if (selectedLabels.length > 0) {
            const andLabel = t(`search.filters.summary.and.label`);
            const languageLabels = selectedLabels.reduce((previous, current, index) => {
                const separator = index < selectedLabels.length - 1 ? ', ' : ` ${andLabel} `;
                return (previous || '') + separator + current;
            });
            return t('search.filters.summary.lang.label', [languageLabels]);
        } else {
            return undefined;
        }
    });

    function getBadgeLabel(badge: RetrieveAvailableFiltersBadgeEnum) {
        if (badge === 'SUPER_MALTER') {
            return t(`search.filters.summary.superMalter.true.label`);
        }
    }

    function getExperienceLevelsLabel(experienceLevels: RetrieveAvailableFiltersExpEnum[]) {
        const experienceLevelsKey = experienceLevels.sort().join('.');
        return t(`search.filters.summary.exp.v2.${experienceLevelsKey}.label` as TranslationKey);
    }

    function getCategoryLabel(categoryId: string) {
        return availableSearchFilters.value?.categories.find((category) => category.key === categoryId)?.label || categoryId;
    }

    function getBusinessSectorLabel(businessSectorHumanId: string) {
        return (
            availableSearchFilters.value?.businessSectors.find((businessSector) => businessSector.key === businessSectorHumanId)?.label ||
            businessSectorHumanId
        );
    }

    function getAdminFilterLabel(adminFilterName: RetrieveAvailableFiltersAdminFilterEnum) {
        if (adminFilterName === 'RECENTLY_MODIFIED') {
            return t('search.filters.summary.recentlyModified.label');
        }
    }

    function unselectLanguagesFilter() {
        selectedFilters.value.languages.forEach((lang) => filtersStore.toggleFilter('languages', lang));
    }

    function unselectExperienceLevelsFilter() {
        selectedFilters.value.experienceLevels.forEach((experienceLevel) => filtersStore.toggleExperienceLevel(experienceLevel));
    }
</script>

<style scoped lang="scss">
    .filters-summary {
        display: flex;
        flex-wrap: wrap;
        position: relative;
        column-gap: var(--joy-core-spacing-2);
        row-gap: var(--joy-core-spacing-3);
    }

    .admin-filter {
        background-color: var(--joy-color-quaternary-10);
    }
</style>
